import type { FC } from 'react';
import { lazy } from 'react';
import { Navigate, Route } from 'react-router';
import { useCapacityManagementRoot } from 'src/apis/capacity-management/use-capacity-management-root';
import { useDeal } from 'src/apis/capacity-management/use-deal';
import { ThrowNotFound } from 'src/components/feedback/not-found';
import { RootRoutes } from 'src/components/root-routes';
import { PageSpinner } from 'src/components/spinner-container';
import { useReferrer } from 'src/hooks/use-referrer';
import { ParamsGuard } from 'src/utils/params-guard';

const DetailsPage = lazy(() => import('./details/page'));
const AddPage = lazy(() => import('./add/page'));
const EditPage = lazy(() => import('./edit/page'));

export const DealAllocationToBalancingGroups: FC = () => {
  const capacityManagementRoot = useCapacityManagementRoot();
  const deal = useDeal({ capacityManagementRoot });
  const referrer = useReferrer({
    label: 'My Transactions',
    location: '/reporting/my-transactions',
  });

  // avoid pop in of page titles in case of 401s
  if (!capacityManagementRoot.response) return <PageSpinner />;

  return (
    <RootRoutes>
      <Route
        path="/reporting/my-transactions/deals/:dealId/allocation-to-balancing-groups/:direction/:balancingGroupAllocationId/edit"
        element={
          <ParamsGuard name="direction" regExp={/^(exit|entry)$/}>
            <EditPage
              deal={deal}
              capacityManagementRootResponse={
                capacityManagementRoot.response.data
              }
            />
          </ParamsGuard>
        }
      />

      <Route
        path="/reporting/my-transactions/deals/:dealId/allocation-to-balancing-groups/:direction/add"
        element={
          <ParamsGuard name="direction" regExp={/^(exit|entry)$/}>
            <AddPage
              deal={deal}
              capacityManagementRootResponse={
                capacityManagementRoot.response.data
              }
            />
          </ParamsGuard>
        }
      />

      <Route
        path="/reporting/my-transactions/deals/:dealId/allocation-to-balancing-groups/:direction"
        element={
          <ParamsGuard name="direction" regExp={/^(exit|entry)$/}>
            <DetailsPage
              deal={deal}
              capacityManagementRootResponse={
                capacityManagementRoot.response.data
              }
            />
          </ParamsGuard>
        }
      />

      <Route
        path="/reporting/my-transactions/deals/:dealId/allocation-to-balancing-groups"
        element={
          !deal.response ? (
            <PageSpinner />
          ) : deal.response.data.exit?._links.balancingGroupAllocations ? (
            <Navigate
              replace
              {...referrer.withReferrerLinkProps({
                pathname: `/reporting/my-transactions/deals/${deal.response.data.dealId}/allocation-to-balancing-groups/exit`,
              })}
            />
          ) : (
            <Navigate
              replace
              {...referrer.withReferrerLinkProps({
                pathname: `/reporting/my-transactions/deals/${deal.response.data.dealId}/allocation-to-balancing-groups/entry`,
              })}
            />
          )
        }
      />

      <Route path="*" element={<ThrowNotFound />} />
    </RootRoutes>
  );
};
